import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, map, Subject } from 'rxjs'
import { SideBoardComponent } from './side-board.component'
import { AgenticModeService } from '@app/agentic-assistant/services/agentic-mode.service'

@Injectable({
  providedIn: 'root',
})
export class SideBoardService {
  private instances: SideBoardComponent[] = []
  private _activeInstance: SideBoardComponent | null = null
  public minimizedInstances: SideBoardComponent[] = []
  public instancesUpdated = new Subject<void>()
  private isMaximizedSubject = new BehaviorSubject<boolean>(false);
  isMaximized$ = this.isMaximizedSubject.asObservable();

  // Create a BehaviorSubject to track active instance
  private activeInstance$ = new BehaviorSubject<SideBoardComponent | null>(null)

  // Public Observable that behaves like the old isOpen$
  public isOpen$ = this.activeInstance$.pipe(map((instance) => instance !== null))

  constructor(private agenticModeService: AgenticModeService) {}

  get activeInstance(): SideBoardComponent | null {
    return this._activeInstance
  }

  private handleMaximizeState(instance: SideBoardComponent, isMaximized: boolean) {
    if (instance.options?.isMaximized !== isMaximized) {
      instance.options.isMaximized = isMaximized;
      instance.maximizeChange.emit(isMaximized);
      this.isMaximizedSubject.next(isMaximized);
    }
  }

  private updateActiveInstance(instance: SideBoardComponent | null) {
    this._activeInstance = instance;
    this.activeInstance$.next(instance);
    this.instancesUpdated.next();
  }

  register(instance: SideBoardComponent): void {
    this.instances.push(instance)
    
    // Check if should be minimized (explicitly set or in agentic mode)
    const shouldMinimize = instance.options?.isMinimized || instance.isMinimized || this.agenticModeService.currentMode === 'agents'
    
    if (shouldMinimize) {
      if (!instance.options) {
        instance.options = { VisibleMini: true, VisibleClose: true, isMinimized: true }
      } else {
        instance.options.isMinimized = true
      }
      this.minimizedInstances.push(instance)
      instance.setMinimized(true)
    } else {
      this.activateInstance(instance)
    }

    this.instancesUpdated.next()
  }

  unregister(instance: SideBoardComponent): void {
    const index = this.instances.indexOf(instance)
    if (index > -1) {
      this.instances.splice(index, 1)
    }
    this.minimizedInstances = this.minimizedInstances.filter((i) => i !== instance)

    if (this._activeInstance === instance) {
      this.updateActiveInstance(null);
    }

    this.handleMaximizeState(instance, false);
  }

  minimizeInstance(instance: SideBoardComponent): void {
    if (!this.minimizedInstances.includes(instance)) {
      this.minimizedInstances.push(instance)
    }

    this.handleMaximizeState(instance, false);

    if (this._activeInstance === instance) {
      this.updateActiveInstance(null);
    }
  }

  activateInstance(instance: SideBoardComponent): void {
    // Don't activate if in agentic mode - keep it minimized
    if (this.agenticModeService.currentMode === 'agents') {
      if (!instance.isMinimized) {
        instance.setMinimized(true)
        if (instance.options) {
          instance.options.isMinimized = true
        }
        this.minimizeInstance(instance)
      }
      return
    }

    // Minimize all other instances
    this.instances.forEach((comp) => {
      if (comp !== instance) {
        comp.setMinimized(true);
        this.handleMaximizeState(comp, false);
      }
    })

    this.updateActiveInstance(instance);
    this.minimizedInstances = this.instances.filter((i) => i !== instance)
    instance.setMinimized(false);
    this.isMaximizedSubject.next(instance.options?.isMaximized || false);
  }

  setMaximized(isMaximized: boolean) {
    this.isMaximizedSubject.next(isMaximized);
  }
}
